import { useEffect } from 'react';

import { downloadCsv } from '@foradevelopment/fora-ui';
import { ReportingReceiptViewModel } from '@fora-monorepo/employer-my-pages-common/types';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';
import { trackCustomEvent } from '@fora-monorepo/webapp-analytics';

import { FileReporting } from '../../components';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getPayrollDetailsCSVFromFileUid, sendFileReport, uploadFileReport } from '../../redux/file/fileThunks';
import { resetFileState } from '../../redux/file/fileSlice';
import { envConfig } from '../../config';
import { FORA_REPORTER_NAME, KS_ADMIN_ROLE } from '../../common/constants';
import { formatFileName } from '../../common/utils';
import { useTypedTranslation } from '../../i18n/i18n';

export const FileReportingSection = ({ reloadReportData }: { reloadReportData: () => void }) => {
  const dispatch = useAppDispatch();

  const fileResponse = useAppSelector(state => state.file.fileResponse);
  const receiptResponse = useAppSelector(state => state.file.receiptResponse);
  const fileUploadErrors = useAppSelector(state => state.file.fileUploadErrors);
  const loadingUploadFileReport = useAppSelector(state => state.file.loadingUploadFileReport);
  const user = useAppSelector(state => state.auth.user);
  const useCompression = useAppSelector(state => state.foraGlobal.settings.ffCompressFileUpload);

  const { faqListReportWithFile, faqListReportWithFileConfirmStep, faqListReportWithFileReceiptStep } = useAppSelector(
    state => state.content.content
  );

  const { t } = useTypedTranslation();

  useEffect(() => {
    return function cleanup() {
      // componentWillUnmount
      dispatch(resetFileState());
    };
  }, [dispatch]);

  const sendFile = (file: File | null) => {
    if (file === null) {
      dispatch(resetFileState());
    } else {
      // Clear potential previous errors before uploading a new file
      dispatch(resetFileState());
      dispatch(uploadFileReport({ file, useCompression }));
      trackCustomEvent('File reporting - uploaded file');
    }
  };

  const sendConfirmationFileUpload = () => {
    if (fileResponse !== null) {
      dispatch(sendFileReport(fileResponse.fileReportUploadUID));
      trackCustomEvent('File reporting - sent file');
    }
  };

  const downloadPayrollDetailsCSV = async (
    fileReportUploadUid: string,
    reportIndex: number,
    organizationNumber: string,
    employerName: string
  ) => {
    const result = await dispatch(
      getPayrollDetailsCSVFromFileUid({
        fileReportUploadUid,
        reportIndex,
        reportedAtTimestamp:
          receiptResponse?.find(r => r.fileReportUploadUid === fileReportUploadUid)?.reportDate ?? '',
      })
    );
    if (getPayrollDetailsCSVFromFileUid.fulfilled.match(result)) {
      if (result.payload) {
        downloadCsv(
          result.payload.base64EncodedFile,
          formatFileName({
            fileType: 'report-details',
            createdDate: receiptResponse?.find(r => r.fileReportUploadUid === fileReportUploadUid)?.reportDate ?? '',
            organizationNumber,
            employerName,
          })
        );
        trackCustomEvent('File reporting - downloaded reported payroll details CSV');
        return null;
      }
      return t('errorMessages.somethingWentWrongNoFileTryLater');
    } else {
      return t('errorMessages.somethingWentWrongWithCsvTryAgain');
    }
  };

  const getReportSummaryModalData = async (index: number): Promise<ReportingReceiptViewModel> => {
    const summary = fileResponse?.fileReportSummaries?.[index];
    const sendResponse = receiptResponse?.[index];
    return {
      organizationName: summary?.organizationName ?? '',
      organizationNumber: summary?.organizationNumber ?? '',
      payMonthSummaries:
        summary?.reportPeriods?.map(p => ({
          numberOfPays: p.payrollsReported,
          payMonth: p.payMonth,
          totalSum: p.payrollAmount,
        })) || [],
      reportDate: sendResponse?.reportDate ?? '',
      reporterName: user?.role === KS_ADMIN_ROLE ? FORA_REPORTER_NAME : `${user?.firstName} ${user?.lastName}`,
      source: sendResponse?.source,
      employmentCount: summary?.totalEmploymentsReported || undefined,
    };
  };

  return (
    <FileReporting
      devLogging={envConfig.devLogging}
      fileResponse={fileResponse}
      sendFile={sendFile}
      sendConfirmation={sendConfirmationFileUpload}
      receiptResponse={receiptResponse}
      loading={loadingUploadFileReport === ThunkRequestStatus.Pending}
      fileUploadErrors={fileUploadErrors}
      faqsUploadStep={faqListReportWithFile}
      faqsReviewAndConfirmStep={faqListReportWithFileConfirmStep}
      faqsReceiptStep={faqListReportWithFileReceiptStep}
      downloadPayrollDetailsCSV={downloadPayrollDetailsCSV}
      getReportSummaryModalData={getReportSummaryModalData}
      reloadReportData={reloadReportData}
    />
  );
};
